"use client";

import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import {
  Home,
  ChevronRight,
  Dog,
  Cat,
  PawPrint,
  Syringe,
  AlertCircle,
  Globe,
  Mail,
  Phone,
  Download,
  Check,
  type LucideIcon,
} from "lucide-react";
import { GiRat } from "react-icons/gi";
import type { IconType } from "react-icons";

const NAVY = "#102542";
const YELLOW = "#ffb800";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Movimiento%20de%20pasajeros/321A9522.jpg";

type Vacuna = { animal: string; icon: string; vacunas: string[] };
type Pago = { concepto: string; monto: string; nota: string };
type Paso = { title: string; subtitle: string; description: string };
type Entidad = {
  nombre: string;
  subtitulo: string;
  web: string;
  email: string;
  telefonos: string[];
};

const ANIMAL_ICONS: Record<string, LucideIcon | IconType> = {
  dog: Dog,
  cat: Cat,
  ferret: GiRat,
};

export default function MascotasPage() {
  const t = useTranslations("mascotasPage");
  const tCrumb = useTranslations("breadcrumb");
  const tNav = useTranslations("nav.informacion.links");
  const locale = useLocale();

  const vacunas = t.raw("requisitos.vacunasItems") as Vacuna[];
  const notas = t.raw("requisitos.notas") as string[];
  const pagos = t.raw("pagos.items") as Pago[];
  const pasos = t.raw("procedimiento.pasos") as Paso[];
  const entidades = t.raw("contacto.entidades") as Entidad[];

  const downloadPDF = () => {
    const link = document.createElement("a");
    link.href = "/formulario_cuarentena_domiciliaria.pdf";
    link.download = "Solicitud de Cuarentena Domiciliaria.pdf";
    link.click();
  };

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("hero.title")}
          fill
          priority
          sizes="100vw"
          className="object-cover blur-xs"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-xs text-white/80 flex-wrap">
              <li>
                <Link
                  href={`/${locale}`}
                  className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                >
                  <Home className="w-3.5 h-3.5" />
                  <span>{tCrumb("home")}</span>
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li>
                <Link
                  href={`/${locale}/informacion`}
                  className="hover:text-white transition-colors"
                >
                  {tNav("informacion")}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="w-3.5 h-3.5 text-white/40" />
              </li>
              <li
                aria-current="page"
                className="text-white font-medium truncate"
              >
                {t("breadcrumb")}
              </li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px" style={{ backgroundColor: YELLOW }} />
              {t("hero.eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("hero.title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Requisitos + Vacunas */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("requisitos.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("requisitos.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("requisitos.subtitle")}
            </p>
          </div>

          {/* Vacunas grid */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Syringe className="w-4 h-4" style={{ color: NAVY }} />
              <span className="text-[11px] font-mono uppercase tracking-wider text-gray-500">
                {t("requisitos.vacunasLabel")}
              </span>
              <span
                className="flex-1 h-px"
                style={{ backgroundColor: YELLOW, maxWidth: 32 }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {vacunas.map((v, idx) => {
                const Icon = ANIMAL_ICONS[v.icon] ?? PawPrint;
                return (
                  <article
                    key={v.animal}
                    className="group bg-white p-8 flex flex-col transition-colors hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-[11px] font-mono uppercase tracking-wider text-gray-400 tabular-nums">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "#f5f5f5", color: NAVY }}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 tracking-tight mb-4">
                      {v.animal}
                    </h3>
                    <ul className="space-y-2 mt-auto">
                      {v.vacunas.map((vac) => (
                        <li
                          key={vac}
                          className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed"
                        >
                          <Check
                            className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                            style={{ color: NAVY }}
                          />
                          <span>{vac}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                );
              })}
            </div>
          </div>

          {/* Notas */}
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <AlertCircle className="w-4 h-4" style={{ color: NAVY }} />
              <span className="text-[11px] font-mono uppercase tracking-wider text-gray-500">
                {t("requisitos.notasLabel")}
              </span>
            </div>
            <ul className="space-y-3">
              {notas.map((nota, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm text-gray-700 leading-relaxed pl-4 border-l-2 border-gray-200"
                >
                  <span>{nota}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Pagos */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("pagos.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("pagos.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("pagos.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {pagos.map((p, idx) => (
              <article
                key={p.concepto}
                className="group relative bg-white p-8 flex flex-col transition-colors hover:bg-gray-50"
              >
                <span
                  className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"
                  style={{ backgroundColor: YELLOW }}
                />
                <span className="text-[11px] font-mono uppercase tracking-wider text-gray-400 tabular-nums mb-6">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="flex items-baseline gap-1 mb-4">
                  <span
                    className="text-sm font-semibold"
                    style={{ color: NAVY }}
                  >
                    {t("pagos.currency")}
                  </span>
                  <span
                    className="text-5xl font-bold tabular-nums tracking-tight leading-none"
                    style={{ color: NAVY }}
                  >
                    {p.monto.split(".")[0]}
                  </span>
                  <span
                    className="text-xl font-bold tabular-nums"
                    style={{ color: NAVY }}
                  >
                    .{p.monto.split(".")[1] ?? "00"}
                  </span>
                </div>
                <h3 className="text-base font-bold text-gray-900 tracking-tight mb-2">
                  {p.concepto}
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {p.nota}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Procedimiento */}
      <section className="bg-white">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("procedimiento.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("procedimiento.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("procedimiento.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {pasos.map((paso, idx) => (
              <article
                key={paso.title}
                className="bg-white p-10 flex flex-col gap-4 transition-colors hover:bg-gray-50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className="text-4xl font-bold tabular-nums tracking-tight leading-none"
                    style={{ color: NAVY }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span
                    className="flex-1 h-px"
                    style={{ backgroundColor: YELLOW, maxWidth: 48 }}
                  />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
                    {paso.subtitle}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 tracking-tight">
                  {paso.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {paso.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contacto */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-[80%] mx-auto py-24">
          <div className="max-w-3xl mb-14">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
              style={{ color: NAVY }}
            >
              <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
              {t("contacto.eyebrow")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
              {t("contacto.title")}
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              {t("contacto.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {entidades.map((ent) => (
              <article
                key={ent.nombre}
                className="bg-white p-8 flex flex-col gap-5 transition-colors hover:bg-gray-50"
              >
                <div className="flex items-baseline gap-3 pb-5 border-b border-gray-100">
                  <span
                    className="text-2xl font-bold tracking-tight"
                    style={{ color: NAVY }}
                  >
                    {ent.nombre}
                  </span>
                  <span className="text-xs text-gray-500 leading-snug">
                    {ent.subtitulo}
                  </span>
                </div>

                <a
                  href={`https://${ent.web}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-3 text-sm"
                >
                  <Globe className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0 group-hover:text-[#102542] transition-colors" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-0.5">
                      {t("contacto.webLabel")}
                    </p>
                    <p className="text-gray-900 truncate group-hover:text-[#102542] transition-colors">
                      {ent.web}
                    </p>
                  </div>
                </a>

                <a
                  href={`mailto:${ent.email}`}
                  className="group flex items-start gap-3 text-sm"
                >
                  <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0 group-hover:text-[#102542] transition-colors" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-0.5">
                      {t("contacto.emailLabel")}
                    </p>
                    <p className="text-gray-900 truncate group-hover:text-[#102542] transition-colors">
                      {ent.email}
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-3 text-sm">
                  <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1">
                      {t("contacto.telefonoLabel")}
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {ent.telefonos.map((tel) => (
                        <a
                          key={tel}
                          href={`tel:${tel.replace(/[^\d+]/g, "")}`}
                          className="text-gray-900 tabular-nums hover:text-[#102542] transition-colors"
                        >
                          {tel}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA descarga */}
      <section className="bg-[#102542]">
        <div className="max-w-[80%] mx-auto py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
                <span
                  className="w-8 h-px"
                  style={{ backgroundColor: YELLOW }}
                />
                {t("cta.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                {t("cta.title")}
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
                {t("cta.subtitle")}
              </p>

              <button
                type="button"
                onClick={downloadPDF}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-wider font-semibold transition-colors hover:opacity-90"
                style={{ backgroundColor: YELLOW, color: NAVY }}
              >
                <Download className="w-3.5 h-3.5" />
                {t("cta.download")}
              </button>
            </div>

            <div className="bg-[#102542] rounded-2xl border border-white/10 p-8">
              <p className="text-[10px] font-mono uppercase tracking-wider text-white/50 mb-4">
                {t("source")}
              </p>
              <div className="border-t border-white/10 pt-4 flex items-center gap-3 text-white/60">
                <PawPrint className="w-4 h-4" style={{ color: YELLOW }} />
                <span className="text-xs">
                  Aeropuerto Internacional de Tocumen
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
